import { useQuery } from "@tanstack/react-query";
import {
  Button,
  FileButton,
  Image,
  MultiSelect,
  Select,
  Switch,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Upload, Video } from "lucide-react";
import dayjs from "dayjs";
import React, { useEffect } from "react";

import { getLanguage } from "../../utils/api";
import { videoAPI } from "../../services/bunnyApi";
import { useGenres } from "../../utils/queries";
import useStore from "../../utils/store";
import UserUploadDataFirebase from "../common/userUploadDataFirebase";
import BunnyVideoSelector from "../common/BunnyVideoSelector";

const AGE_RATINGS = [
  { label: "13+", value: "13+" },
  { label: "16+", value: "16+" },
  { label: "18+", value: "18+" },
  { label: "U", value: "U" },
  { label: "U/A", value: "U/A" },
  { label: "A", value: "A" },
];

const VideoForm = ({ dataType = "Movie", type = "Movie", form, onFinish, submit, update }) => {
  const user = useStore((state) => state.user);
  const { data: genres } = useGenres();
  const { data: languages } = useQuery({ queryKey: ["languages"], queryFn: getLanguage });

  const [formValues, setFormValues] = React.useState({
    name: "", description: "", genres: [], url: "", duration: "", releaseDate: "",
    trailer: "", ageLimit: "", language: "", episodeNumber: "", position: "",
    subtitles: [], isFeatured: false, isEnable: false, status: "Pending",
    videoId: "", mp4UrlArray: "", mp4Url: "", stripEnable: false, stripText: "",
  });
  const [horizontalFile, setHorizontalFile] = React.useState(null);
  const [verticalFile, setVerticalFile] = React.useState(null);
  const [isBunnyModalOpen, setIsBunnyModalOpen] = React.useState(false);
  const [isBunnyTrailerOpen, setIsBunnyTrailerOpen] = React.useState(false);

  useEffect(() => {
    if (update) {
      setFormValues({
        name: update?.name || "", description: update?.description || "",
        genres: update?.genres?.map((item) => item._id) || [],
        url: update?.url || "", duration: update?.duration || "",
        releaseDate: update?.releaseDate ? dayjs(update.releaseDate).format("YYYY-MM-DD") : "",
        trailer: update?.trailer || "", ageLimit: update?.ageLimit || "",
        language: update?.language || "", episodeNumber: update?.episode || "",
        position: update?.position || "", subtitles: update?.subtitles || [],
        isFeatured: update?.isFeatured || false,
        isEnable: update?.isEnable || false, status: update?.status || "Pending",
        videoId: update?.videoId || "",
        mp4UrlArray:
          update?.mp4UrlArray == null
            ? ""
            : typeof update.mp4UrlArray === "string"
            ? update.mp4UrlArray
            : JSON.stringify(update.mp4UrlArray),
        mp4Url: update?.mp4Url || "", stripEnable: update?.stripEnable || false,
        stripText: update?.stripText || "",
      });
    }
  }, [update]);

  const handleChange = (field, value) => setFormValues((prev) => ({ ...prev, [field]: value }));

  const handleSelectBunnyVideo = (video) => {
    const url = videoAPI.getPlaylistUrl(video.guid);
    const mp4UrlMap = videoAPI.getMp4UrlMap(video.guid);
    const mp4Url480p = videoAPI.getMp4Url(video.guid, "480p");
    setFormValues((prev) => ({
      ...prev,
      url,
      videoId: video.guid,
      mp4UrlArray: JSON.stringify(mp4UrlMap),
      mp4Url: mp4Url480p,
    }));
    notifications.show({ message: `"${video.title}" selected`, color: "green" });
    setIsBunnyModalOpen(false);
  };

  const handleSelectBunnyTrailer = (video) => {
    const url = videoAPI.getPlaylistUrl(video.guid);
    setFormValues((prev) => ({ ...prev, trailer: url }));
    notifications.show({ message: `Trailer "${video.title}" selected`, color: "green" });
    setIsBunnyTrailerOpen(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    if (horizontalFile) formData.append("horizontalBanner", horizontalFile);
    if (verticalFile) formData.append("verticalBanner", verticalFile);

    const basicInfo = {
      type: dataType, name: formValues.name, description: formValues.description,
      view: "0", genres: JSON.stringify(formValues.genres), url: formValues.url || "",
      trailer: formValues.trailer || "", language: formValues.language,
      ageLimit: formValues.ageLimit, duration: formValues.duration,
      position: formValues.position, isFeatured: formValues.isFeatured,
      videoId: formValues.videoId,
      mp4UrlArray:
        formValues.mp4UrlArray == null
          ? undefined
          : typeof formValues.mp4UrlArray === "string"
          ? formValues.mp4UrlArray
          : JSON.stringify(formValues.mp4UrlArray),
      mp4Url: formValues.mp4Url, stripEnable: formValues.stripEnable,
      stripText: formValues.stripText,
      ...(user?.isPublishAccess && { isEnable: formValues.isEnable, status: formValues.status }),
    };

    Object.entries(basicInfo).forEach(([key, value]) => {
      if (value !== undefined) formData.append(key, value);
    });

    if (type === "Episode" && formValues.episodeNumber) formData.append("episodeNumber", formValues.episodeNumber);
    if (type !== "Series" && type !== "Coming Soon") formData.append("subtitles", JSON.stringify(formValues.subtitles));
    if (type === "Movie" || type === "Series" || type === "Coming Soon") {
      formData.append("releaseDate", dayjs(formValues.releaseDate).toISOString());
    }

    onFinish(formData);
  };

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
      <TextInput label="Name" required value={formValues.name} onChange={(e) => handleChange("name", e.target.value)} />
      <Textarea label="Description" required value={formValues.description} onChange={(e) => handleChange("description", e.target.value)} />

      {type !== "Series" && type !== "Coming Soon" && (
        <>
          <div>
            <Text size="sm" fw={500} mb={4}>Video URL (Bunny)</Text>
            <div className="flex gap-2 items-center">
              <TextInput
                style={{ flex: 1 }}
                value={formValues.url}
                readOnly
                placeholder="Select a video from Bunny library"
              />
              <Button
                leftSection={<Video size={16} />}
                onClick={() => setIsBunnyModalOpen(true)}
                variant="default"
              >
                Select Video
              </Button>
            </div>
            {formValues.videoId && (
              <div className="text-xs app-text-muted mt-1">
                Video ID: <span className="font-mono">{formValues.videoId}</span>
              </div>
            )}
          </div>
          <TextInput label="Duration" value={formValues.duration} onChange={(e) => handleChange("duration", e.target.value)} placeholder="Enter duration" />

          <BunnyVideoSelector
            open={isBunnyModalOpen}
            onCancel={() => setIsBunnyModalOpen(false)}
            onSelect={handleSelectBunnyVideo}
            title="Select Video (Bunny)"
            verticalOnly={false}
          />
        </>
      )}

      {dataType !== "Music Video" && (
        <div>
          <Text size="sm" fw={500} mb={4}>Trailer</Text>
          <div className="flex gap-2 items-center">
            <TextInput
              style={{ flex: 1 }}
              value={formValues.trailer}
              readOnly
              placeholder="Select a trailer from Bunny library"
            />
            <Button
              leftSection={<Video size={16} />}
              onClick={() => setIsBunnyTrailerOpen(true)}
              variant="default"
            >
              Select Trailer
            </Button>
          </div>
        </div>
      )}

      {(type === "Movie" || type === "Series" || type === "Coming Soon") && (
        <TextInput label="Release Date" type="date" required value={formValues.releaseDate} onChange={(e) => handleChange("releaseDate", e.target.value)} />
      )}

      {type !== "Coming Soon" && (
        <Select label="Language" required placeholder="Select language" searchable
          data={(languages || []).map((item) => ({ label: item.name, value: item.name }))}
          value={formValues.language || null} onChange={(val) => handleChange("language", val)} />
      )}

      <Select label="Age Limit" required data={AGE_RATINGS} value={formValues.ageLimit || null} onChange={(val) => handleChange("ageLimit", val)} />

      <MultiSelect label="Genres" required searchable placeholder="Select genres"
        data={(genres || []).map((item) => ({ label: item.name, value: item._id }))}
        value={formValues.genres} onChange={(val) => handleChange("genres", val)} />

      {type === "Episode" && (
        <TextInput label="Episode Number" required value={formValues.episodeNumber} onChange={(e) => handleChange("episodeNumber", e.target.value)} />
      )}

      <div>
        <Text size="sm" fw={500} mb={4}>Horizontal Banner (1280 x 720 px) {!update && <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>}</Text>
        {update?.horizontalBanner && <div className="mb-2"><Image src={update.horizontalBanner} alt="Horizontal Banner Preview" mah={200} fit="contain" /></div>}
        <FileButton onChange={(file) => setHorizontalFile(file)} accept="image/*">
          {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>{horizontalFile ? horizontalFile.name : "Upload"}</Button>}
        </FileButton>
      </div>

      <div>
        <Text size="sm" fw={500} mb={4}>Vertical Banner (900 x 1200 px) {!update && <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>}</Text>
        {update?.verticalBanner && <div className="mb-2"><Image src={update.verticalBanner} alt="Vertical Banner Preview" mah={200} fit="contain" /></div>}
        <FileButton onChange={(file) => setVerticalFile(file)} accept="image/*">
          {(props) => <Button variant="default" leftSection={<Upload size={16} />} {...props}>{verticalFile ? verticalFile.name : "Upload"}</Button>}
        </FileButton>
      </div>

      {type !== "Series" && type !== "Coming Soon" && (
        <div>
          <Text size="sm" fw={500} mb={4}>Subtitles</Text>
          <div className="flex flex-col gap-3">
            {(formValues.subtitles || []).map((sub, index) => (
              <div key={index} className="flex flex-col gap-2 border border-zinc-700 p-3 rounded">
                <TextInput placeholder="Enter language" value={sub.language}
                  onChange={(e) => {
                    const newSubs = [...formValues.subtitles];
                    newSubs[index] = { ...newSubs[index], language: e.target.value };
                    handleChange("subtitles", newSubs);
                  }} />
                <UserUploadDataFirebase type="srt" label="Subtitles" update={true}
                  onChange={(url) => {
                    const newSubs = [...formValues.subtitles];
                    newSubs[index] = { ...newSubs[index], link: url };
                    handleChange("subtitles", newSubs);
                  }} />
                <Button color="red" variant="subtle" size="xs" onClick={() => handleChange("subtitles", formValues.subtitles.filter((_, i) => i !== index))}>Delete</Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => handleChange("subtitles", [...(formValues.subtitles || []), { language: "", link: "" }])} fullWidth>Add Subtitle</Button>
          </div>
        </div>
      )}

      {type === "Coming Soon" && (
        <TextInput label="Position" value={formValues.position} onChange={(e) => handleChange("position", e.target.value)} />
      )}

      {user?.isPublishAccess && type !== "Coming Soon" && (
        <>
          <Select label="Status" data={[{ value: "Approved", label: "Approved" }, { value: "Rejected", label: "Rejected" }, { value: "Pending", label: "Pending" }]}
            value={formValues.status} onChange={(val) => handleChange("status", val)} />
          <Switch label={formValues.isEnable ? "Enabled" : "Disabled"} checked={formValues.isEnable} onChange={(e) => handleChange("isEnable", e.currentTarget.checked)} />
        </>
      )}

      {type === "Movie" && (
        <Switch label={formValues.isFeatured ? "Featured" : "Not Featured"} checked={formValues.isFeatured} onChange={(e) => handleChange("isFeatured", e.currentTarget.checked)} />
      )}

      {(type === "Movie" || type === "Series") && (
        <>
          <Switch label={formValues.stripEnable ? "Strip Enabled" : "Strip Disabled"} checked={formValues.stripEnable} onChange={(e) => handleChange("stripEnable", e.currentTarget.checked)} />
          <Textarea label="Strip Text" placeholder="Enter strip text" rows={3} disabled={!formValues.stripEnable}
            value={formValues.stripText} onChange={(e) => handleChange("stripText", e.target.value)} />
        </>
      )}

      {submit}

      <BunnyVideoSelector
        open={isBunnyTrailerOpen}
        onCancel={() => setIsBunnyTrailerOpen(false)}
        onSelect={handleSelectBunnyTrailer}
        title="Select Trailer (Bunny)"
        verticalOnly={false}
      />
    </form>
  );
};

export default VideoForm;
